import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import TrueWellnessHeader from "@/components/truewellness/TrueWellnessHeader";
import TrueWellnessFooter from "@/components/truewellness/TrueWellnessFooter";
import LearnMoreModal from "@/components/truewellness/LearnMoreModal";
import { wellnessBlogPosts } from "@/data/wellnessBlogPosts";

// Import images
import glp1Background from "@/assets/glp1-weight-loss.png";
import weightLossJourneyHero from "@/assets/weight-loss-journey-hero.png";
import trueenergyBackground from "@/assets/trueenergy-background.png";
import truemoodBackground from "@/assets/truemood-background.png";
import truesleepBackground from "@/assets/truesleep-background.png";
import truehairBackground from "@/assets/truehair-background.png";
import trueskinBackground from "@/assets/trueskin-background.png";
import truerecoveryBackground from "@/assets/truerecovery-background.png";
import employerBenefitsBackground from "@/assets/employer-benefits-background.png";
import heroImage from "@/assets/hero-image.png";
import springBreakStack from "@/assets/spring-break-stack.jpg";
import summerMaintenanceStack from "@/assets/summer-maintenance-stack.png";
import fluDefenseStack from "@/assets/flu-defense-stack.png";
import gutRestorationStack from "@/assets/gut-restoration-stack.png";
import surgeryStack from "@/assets/surgery-stack.jpg";
import athleticStack from "@/assets/athletic-stack.jpg";
import stressBurnoutStack from "@/assets/stress-burnout-stack.png";
import longevityStack from "@/assets/longevity-stack.jpg";
import metabolicResetStack from "@/assets/metabolic-reset-stack.jpg";
import executivePerformanceStack from "@/assets/executive-performance-stack.png";
import radianceStack from "@/assets/radiance-skin-stack.png";
import harmonyStack from "@/assets/harmony-stack.png";

const imageMap: Record<string, string> = {
  "glp1-weight-loss": weightLossJourneyHero,
  "energy": trueenergyBackground,
  "mood": truemoodBackground,
  "sleep": truesleepBackground,
  "hair": truehairBackground,
  "skin": trueskinBackground,
  "recovery": truerecoveryBackground,
  "employer-benefits": employerBenefitsBackground,
  "hero": heroImage,
  "spring-break": springBreakStack,
  "summer": summerMaintenanceStack,
  "flu-defense": fluDefenseStack,
  "gut-restoration": gutRestorationStack,
  "surgery": surgeryStack,
  "athletic": athleticStack,
  "stress-burnout": stressBurnoutStack,
  "longevity": longevityStack,
  "metabolic": metabolicResetStack,
  "executive-performance": executivePerformanceStack,
  "radiance": radianceStack,
  "harmony": harmonyStack,
};

const WellnessBlog = () => {
  const navigate = useNavigate();
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const handleOpenModal = () => {
    setLearnMoreOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>TrueWellness Blog - Affordable GLP-1, Peptide Therapy & Weight Loss Guide</title>
        <meta 
          name="description" 
          content="Discover affordable GLP-1 weight loss options, personalized peptide therapy, and science-backed wellness protocols. Expert guides on semaglutide, tirzepatide, and regenerative medicine." 
        />
        <meta 
          name="keywords" 
          content="affordable glp-1, cheap glp-1, semaglutide weight loss, tirzepatide, personalized peptides, peptide therapy, medical weight loss, BPC-157, NAD+, regenerative medicine, weight loss medication" 
        />
        <meta property="og:title" content="TrueWellness Blog - Affordable GLP-1 & Peptide Therapy Guide" />
        <meta property="og:description" content="Your source for affordable medical weight loss and personalized peptide therapy information." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.mytruewellness.com/wellness/blog" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <TrueWellnessHeader onOpenModal={handleOpenModal} />
        
        <main className="container mx-auto px-4 py-24 md:py-32">
          <header className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-2 bg-blue-500/10 text-blue-500 font-semibold rounded-full text-sm mb-4">
              TrueWellness Blog
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-blue-600 mb-4">
              Your Guide to <span className="text-blue-500">Affordable Wellness</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
              Expert insights on GLP-1 weight loss, personalized peptide therapy, and science-backed health optimization
            </p>
          </header>

          <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {wellnessBlogPosts.map((post) => (
              <Card 
                key={post.id}
                className="hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                onClick={() => navigate(`/wellness/blog/${post.id}`)}
              >
                {/* Image */}
                <div 
                  className="h-48 bg-cover bg-center relative overflow-hidden"
                  style={{ backgroundImage: `url(${imageMap[post.image]})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent group-hover:from-black/40 transition-all" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#ffdb4d]/10" />
                </div>
                <CardHeader className="pb-2">
                  <div className="text-sm text-wellness font-semibold mb-1">
                    {post.category}
                  </div>
                  <CardTitle className="group-hover:text-wellness transition-colors line-clamp-2">
                    {post.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    {new Date(post.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground line-clamp-3">
                    {post.excerpt}
                  </p>
                </CardContent>
              </Card>
            ))}
          </section>
        </main>

        <TrueWellnessFooter />
        <LearnMoreModal open={learnMoreOpen} onOpenChange={setLearnMoreOpen} />
      </div>
    </>
  );
};

export default WellnessBlog;
